<template>
    <div>
        <div class="video-thumbnail" @click="openOverlay" :style="containerStyle">
            <video ref="previewVideo" :src="src" class="preview-video" @loadedmetadata="onLoadedMetadata"></video>
            <div class="play-overlay">
                <div class="play-button">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="white">
                        <path d="M8 5v14l11-7z" />
                    </svg>
                </div>
            </div>
            <div class="video-info" @click.stop>
                <div class="video-duration" v-if="duration" :title="durationTooltip">
                    <svg class="video-duration-icon" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z" />
                    </svg>
                    <span>{{ formatTime(duration) }}</span>
                </div>
                <div
                    class="video-timestamp-wrap"
                    v-if="timestamp"
                    @mouseenter="showTimestampTooltip = true"
                    @mouseleave="showTimestampTooltip = false"
                >
                    <div class="video-timestamp">{{ timestamp }}</div>
                    <div v-if="showTimestampTooltip && timestampTooltip" class="video-timestamp-tooltip">
                        {{ timestampTooltip }}
                    </div>
                </div>
            </div>
        </div>

        <div v-if="showOverlay" class="video-overlay" @click.self="closeOverlay">
            <div class="overlay-content" @click.stop>
                <button class="close-button" @click="closeOverlay">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                        <path
                            d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
                        />
                    </svg>
                </button>

                <div class="video-container" ref="playerContainer">
                    <video
                        ref="videoElement"
                        :src="src"
                        @loadedmetadata="onOverlayVideoMetadata"
                        @play="onVideoPlay"
                        @pause="onVideoPause"
                        @timeupdate="onTimeUpdate"
                        @ended="onEnded"
                        @click="togglePlay"
                        class="video-element"
                        autoplay
                    ></video>

                    <div class="controls" :class="{ show: showControls }">
                        <div
                            class="progress-container"
                            @click="seek"
                            @mousedown="startSeeking"
                            @mousemove="updateHoverTime"
                            @mouseleave="hideHoverTime"
                        >
                            <div class="progress-bar">
                                <div class="progress-filled" :style="{ width: progressPercent + '%' }"></div>
                                <div
                                    v-if="isSeeking && seekTime !== null"
                                    class="hover-time seek-time"
                                    :style="{ left: seekTimePosition + '%' }"
                                >
                                    {{ formatTime(seekTime) }}
                                </div>
                                <div
                                    v-else-if="hoverTime !== null"
                                    class="hover-time"
                                    :style="{ left: hoverTimePosition + '%' }"
                                >
                                    {{ formatTime(hoverTime) }}
                                </div>
                            </div>
                        </div>

                        <div class="controls-bottom">
                            <div class="controls-left">
                                <button @click="togglePlay" class="control-btn">
                                    <svg v-if="!isPlaying" width="20" height="20" viewBox="0 0 24 24" fill="white">
                                        <path d="M8 5v14l11-7z" />
                                    </svg>
                                    <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="white">
                                        <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                                    </svg>
                                </button>

                                <button @click="toggleMute" class="control-btn">
                                    <svg
                                        v-if="!isMuted && volume > 0.5"
                                        width="20"
                                        height="20"
                                        viewBox="0 0 24 24"
                                        fill="white"
                                    >
                                        <path
                                            d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"
                                        />
                                    </svg>
                                    <svg
                                        v-else-if="!isMuted && volume > 0"
                                        width="20"
                                        height="20"
                                        viewBox="0 0 24 24"
                                        fill="white"
                                    >
                                        <path d="M7 9v6h4l5 5V4l-5 5H7z" />
                                    </svg>
                                    <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="white">
                                        <path
                                            d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"
                                        />
                                    </svg>
                                </button>

                                <div class="time-display">{{ formatTime(currentTime) }} / {{ formatTime(duration) }}</div>
                            </div>

                            <div class="controls-right">
                                <select v-model="playbackRate" @change="changePlaybackRate" class="playback-rate">
                                    <option v-for="rate in playbackRates" :key="rate" :value="rate">{{ rate }}x</option>
                                </select>

                                <button @click="toggleFullscreen" class="control-btn">
                                    <svg v-if="!isFullscreen" width="20" height="20" viewBox="0 0 24 24" fill="white">
                                        <path
                                            d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"
                                        />
                                    </svg>
                                    <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="white">
                                        <path
                                            d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"
                                        />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "CustomVideoPlayer",
    props: {
        src: {
            type: String,
            required: true,
        },
        poster: {
            type: String,
            default: undefined,
        },
        maxWidth: {
            type: Number,
            default: 400,
        },
        maxHeight: {
            type: Number,
            default: 300,
        },
        playbackRates: {
            type: Array,
            default: () => [0.5, 0.7, 1.0, 1.5, 2.0],
        },
        muted: {
            type: Boolean,
            default: false,
        },
        timestamp: {
            type: String,
            default: "",
        },
        timestampTooltip: {
            type: String,
            default: "",
        },
    },
    data() {
        return {
            isPlaying: false,
            currentTime: 0,
            duration: 0,
            volume: 1,
            isMuted: this.muted,
            playbackRate: 1.0,
            showControls: true,
            isFullscreen: false,
            isLoading: false,
            controlsTimeout: null,
            videoWidth: 0,
            videoHeight: 0,
            showOverlay: false,
            isSeeking: false,
            hoverTime: null,
            hoverTimePosition: 0,
            seekTime: null,
            seekTimePosition: 0,
            seekingFrame: null,
            showTimestampTooltip: false,
        };
    },
    computed: {
        progressPercent() {
            if (!this.duration) return 0;
            return (this.currentTime / this.duration) * 100;
        },
        durationTooltip() {
            if (!this.duration || isNaN(this.duration)) return "";
            const m = Math.floor(this.duration / 60);
            const s = Math.floor(this.duration % 60);
            if (m > 0) {
                return `Süre: ${m} dakika ${s} saniye (${this.formatTime(this.duration)})`;
            }
            return `Süre: ${s} saniye (${this.formatTime(this.duration)})`;
        },
        containerStyle() {
            if (!this.videoWidth || !this.videoHeight) {
                return {
                    maxWidth: `${this.maxWidth}px`,
                    maxHeight: `${this.maxHeight}px`,
                };
            }

            const aspectRatio = this.videoWidth / this.videoHeight;
            let width = this.maxWidth;
            let height = width / aspectRatio;

            if (height > this.maxHeight) {
                height = this.maxHeight;
                width = height * aspectRatio;
            }

            return {
                width: `${width}px`,
                height: `${height}px`,
            };
        },
    },
    methods: {
        openOverlay() {
            this.currentTime = 0;
            this.isPlaying = false;
            this.isSeeking = false;
            this.hoverTime = null;
            this.hoverTimePosition = 0;
            this.showOverlay = true;
            document.body.style.overflow = "hidden";
        },

        closeOverlay() {
            const video = this.$refs.videoElement;
            if (video) {
                video.pause();
                video.currentTime = 0;
            }
            
            this.showOverlay = false;
            this.isPlaying = false;
            this.currentTime = 0;
            this.isSeeking = false;
            this.hoverTime = null;
            this.hoverTimePosition = 0;
            this.seekTime = null;
            this.seekTimePosition = 0;
            this.showTimestampTooltip = false;
            this.showControls = true;

            if (this.seekingFrame) {
                cancelAnimationFrame(this.seekingFrame);
                this.seekingFrame = null;
            }
            
            document.body.style.overflow = "";
        },

        togglePlay() {
            const video = this.$refs.videoElement;
            if (!video) return;
            if (video.paused) {
                video.play();
                this.isPlaying = true;
            } else {
                video.pause();
                this.isPlaying = false;
            }
        },

        toggleMute() {
            const video = this.$refs.videoElement;
            this.isMuted = !this.isMuted;
            video.muted = this.isMuted;
        },

        changeVolume() {
            const video = this.$refs.videoElement;
            video.volume = this.volume;
            if (this.volume > 0) {
                this.isMuted = false;
                video.muted = false;
            }
        },

        changePlaybackRate() {
            const video = this.$refs.videoElement;
            video.playbackRate = parseFloat(this.playbackRate);
        },

        seek(event) {
            const video = this.$refs.videoElement;
            const progressBar = event.currentTarget;
            const rect = progressBar.getBoundingClientRect();
            const pos = (event.clientX - rect.left) / rect.width;
            video.currentTime = pos * this.duration;
        },

        startSeeking(event) {
            event.preventDefault();
            this.hoverTime = null;
            this.isSeeking = true;
            this.seekTime = 0;
            this.seekTimePosition = 0;
            this.seek(event);
            this.updateSeekTimeFromEvent(event);
            document.addEventListener("mousemove", this.handleSeekingMove, { passive: false });
            document.addEventListener("mouseup", this.stopSeeking);
        },

        updateSeekTimeFromEvent(event) {
            const progressContainer = event.currentTarget?.closest?.(".progress-container") ?? document.querySelector(".progress-container");
            if (!progressContainer) return;
            const rect = progressContainer.getBoundingClientRect();
            const pos = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
            this.seekTimePosition = pos * 100;
            this.seekTime = pos * this.duration;
        },

        handleSeekingMove(event) {
            if (!this.isSeeking) return;
            event.preventDefault();
            this.updateSeekTimeFromEvent(event);

            if (!this.seekingFrame) {
                this.seekingFrame = requestAnimationFrame(() => {
                    const progressContainer = document.querySelector(".progress-container");
                    if (!progressContainer) {
                        this.seekingFrame = null;
                        return;
                    }
                    const rect = progressContainer.getBoundingClientRect();
                    const pos = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
                    const video = this.$refs.videoElement;
                    if (video) {
                        video.currentTime = pos * this.duration;
                    }
                    this.seekingFrame = null;
                });
            }
        },

        stopSeeking(event) {
            event.preventDefault();
            this.isSeeking = false;
            this.hoverTime = null;
            this.seekTime = null;
            document.removeEventListener("mousemove", this.handleSeekingMove);
            document.removeEventListener("mouseup", this.stopSeeking);
            if (this.seekingFrame) {
                cancelAnimationFrame(this.seekingFrame);
                this.seekingFrame = null;
            }
        },

        updateHoverTime(event) {
            if (this.isSeeking) return;
            const progressBar = event.currentTarget;
            const rect = progressBar.getBoundingClientRect();
            const pos = (event.clientX - rect.left) / rect.width;
            this.hoverTimePosition = pos * 100;
            this.hoverTime = pos * this.duration;
        },

        hideHoverTime() {
            if (!this.isSeeking) {
                this.hoverTime = null;
            }
        },

        toggleFullscreen() {
            const container = this.$refs.playerContainer;
            if (!this.isFullscreen) {
                if (container.requestFullscreen) {
                    container.requestFullscreen();
                } else if (container.webkitRequestFullscreen) {
                    container.webkitRequestFullscreen();
                } else if (container.mozRequestFullScreen) {
                    container.mozRequestFullScreen();
                } else if (container.msRequestFullscreen) {
                    container.msRequestFullscreen();
                }
                this.isFullscreen = true;
            } else {
                if (document.exitFullscreen) {
                    document.exitFullscreen();
                } else if (document.webkitExitFullscreen) {
                    document.webkitExitFullscreen();
                } else if (document.mozCancelFullScreen) {
                    document.mozCancelFullScreen();
                } else if (document.msExitFullscreen) {
                    document.msExitFullscreen();
                }
                this.isFullscreen = false;
            }
        },

        formatTime(time) {
            if (isNaN(time)) return "00:00";
            const minutes = Math.floor(time / 60);
            const seconds = Math.floor(time % 60);
            return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
        },

        onLoadedMetadata(event) {
            const video = event.target;
            this.videoWidth = video.videoWidth;
            this.videoHeight = video.videoHeight;
            this.duration = video.duration;
        },

        onOverlayVideoMetadata(event) {
            const video = event.target;
            this.duration = video.duration;
            video.currentTime = 0;
            this.currentTime = 0;
        },

        onVideoPlay() {
            this.isPlaying = true;
        },

        onVideoPause() {
            this.isPlaying = false;
        },

        onTimeUpdate() {
            const video = this.$refs.videoElement;
            this.currentTime = video.currentTime;
        },

        /**
         * On video ended
         */
        onEnded() {
            this.isPlaying = false;
        },

        showControlsTemporarily() {
            this.showControls = true;
            clearTimeout(this.controlsTimeout);
            this.controlsTimeout = setTimeout(() => {
                if (this.isPlaying) {
                    this.showControls = false;
                }
            }, 3000);
        },

        handleFullscreenChange() {
            this.isFullscreen = !!(
                document.fullscreenElement ||
                document.webkitFullscreenElement ||
                document.mozFullScreenElement ||
                document.msFullscreenElement
            );
        },

        handleEscapeKey(event) {
            if (event.key === "Escape" && this.showOverlay) {
                this.closeOverlay();
            }
        },
    },
    watch: {
        showOverlay(newVal) {
            if (newVal) {
                this.$nextTick(() => {
                    const video = this.$refs.videoElement;
                    const container = this.$refs.playerContainer;

                    if (video) {
                        video.muted = this.isMuted;
                        video.currentTime = 0;
                    }

                    if (container) {
                        container.addEventListener("mousemove", this.showControlsTemporarily);
                    }
                });
            } else {
                const container = this.$refs.playerContainer;
                if (container) {
                    container.removeEventListener("mousemove", this.showControlsTemporarily);
                }
                
                document.removeEventListener("mousemove", this.handleSeekingMove);
                document.removeEventListener("mouseup", this.stopSeeking);
            }
        },
    },
    mounted() {
        document.addEventListener("fullscreenchange", this.handleFullscreenChange);
        document.addEventListener("webkitfullscreenchange", this.handleFullscreenChange);
        document.addEventListener("mozfullscreenchange", this.handleFullscreenChange);
        document.addEventListener("msfullscreenchange", this.handleFullscreenChange);

        document.addEventListener("keydown", this.handleEscapeKey);
    },
    beforeUnmount() {
        const container = this.$refs.playerContainer;
        if (container) {
            container.removeEventListener("mousemove", this.showControlsTemporarily);
        }

        document.removeEventListener("fullscreenchange", this.handleFullscreenChange);
        document.removeEventListener("webkitfullscreenchange", this.handleFullscreenChange);
        document.removeEventListener("mozfullscreenchange", this.handleFullscreenChange);
        document.removeEventListener("msfullscreenchange", this.handleFullscreenChange);
        document.removeEventListener("keydown", this.handleEscapeKey);
        document.removeEventListener("mousemove", this.handleSeekingMove);
        document.removeEventListener("mouseup", this.stopSeeking);

        if (this.seekingFrame) {
            cancelAnimationFrame(this.seekingFrame);
        }

        document.body.style.overflow = "";
        clearTimeout(this.controlsTimeout);
    },
};
</script>

<style lang="scss" scoped>
.video-thumbnail {
    position: relative;
    display: inline-block;
    background: #000;
    overflow: hidden;
    border-radius: 8px;
    cursor: pointer;

    &:hover {
        .play-overlay {
            background: rgba(0, 0, 0, 0.5);
        }
    }

    .preview-video {
        width: 100%;
        height: 100%;
        display: block;
        object-fit: cover;
        pointer-events: none;
    }

    .play-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(0, 0, 0, 0.3);
        transition: background 0.3s;

        .play-button {
            background: rgba(139, 92, 246, 0.9);
            border-radius: 50%;
            width: 60px;
            height: 60px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.3s;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);

            svg {
                margin-left: 3px;
                filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
            }
        }
    }

    .video-info {
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        display: flex;
        justify-content: space-between;
        padding: 8px;
        background: linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent);
        pointer-events: auto;

        .video-duration {
            display: flex;
            align-items: center;
            gap: 5px;
            background: rgba(0, 0, 0, 0.8);
            color: white;
            padding: 3px 8px;
            border-radius: 4px;
            font-size: 12px;
            font-weight: 500;
            text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
            cursor: default;
            pointer-events: auto;

            .video-duration-icon {
                flex-shrink: 0;
                opacity: 0.95;
            }
        }

        .video-timestamp-wrap {
            position: relative;
        }

        .video-timestamp {
            background: rgba(0, 0, 0, 0.8);
            color: white;
            padding: 3px 8px;
            border-radius: 4px;
            font-size: 12px;
            font-weight: 500;
            text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
            cursor: default;
            pointer-events: auto;
        }

        .video-timestamp-tooltip {
            position: absolute;
            bottom: 100%;
            right: 0;
            margin-bottom: 6px;
            background: rgba(0, 0, 0, 0.95);
            color: white;
            padding: 6px 10px;
            border-radius: 6px;
            font-size: 12px;
            white-space: nowrap;
            z-index: 20;
            pointer-events: none;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);

            &::after {
                content: "";
                position: absolute;
                top: 100%;
                right: 12px;
                border: 5px solid transparent;
                border-top-color: rgba(0, 0, 0, 0.95);
            }
        }
    }
}

.video-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.95);
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    animation: fadeIn 0.3s;

    @keyframes fadeIn {
        from {
            opacity: 0;
        }
        to {
            opacity: 1;
        }
    }

    .overlay-content {
        position: relative;
        width: 90vw;
        max-width: 1200px;
        max-height: 90vh;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .close-button {
        position: absolute;
        top: -50px;
        right: 0;
        background: rgba(255, 255, 255, 0.1);
        border: none;
        width: 40px;
        height: 40px;
        border-radius: 50%;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.3s;
        z-index: 10;

        &:hover {
            background: rgba(255, 255, 255, 0.2);
            transform: scale(1.1);
        }

        svg {
            filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.5));
        }
    }

    .video-container {
        position: relative;
        width: 100%;
        height: 100%;
        background: #000;
        border-radius: 8px;
        overflow: hidden;
        user-select: none;

        .video-element {
            width: 100%;
            height: 100%;
            max-height: 90vh;
            display: block;
            cursor: pointer;
            object-fit: contain;
        }

        .controls {
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            background: linear-gradient(to top, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0.5) 50%, transparent 100%);
            padding: 30px 15px 12px;
            opacity: 0;
            transition: opacity 0.3s;

            &.show {
                opacity: 1;
            }
        }

        &:hover .controls {
            opacity: 1;
        }

        .progress-container {
            margin-bottom: 10px;
            cursor: pointer;
            position: relative;
            user-select: none;

            .progress-bar {
                height: 5px;
                background: rgba(255, 255, 255, 0.3);
                border-radius: 3px;
                overflow: visible;
                position: relative;

                .progress-filled {
                    height: 100%;
                    background: #8b5cf6;
                    transition: width 0.05s linear;
                    pointer-events: none;
                }

                .hover-time {
                    position: absolute;
                    bottom: 12px;
                    transform: translateX(-50%);
                    background: rgba(0, 0, 0, 0.9);
                    color: white;
                    padding: 4px 8px;
                    border-radius: 4px;
                    font-size: 12px;
                    white-space: nowrap;
                    pointer-events: none;
                    z-index: 10;

                    &::after {
                        content: "";
                        position: absolute;
                        top: 100%;
                        left: 50%;
                        transform: translateX(-50%);
                        border: 4px solid transparent;
                        border-top-color: rgba(0, 0, 0, 0.9);
                    }
                }
            }

            &:hover .progress-bar {
                height: 7px;
            }
        }

        .controls-bottom {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 10px;
            user-select: none;
        }

        .controls-left,
        .controls-right {
            display: flex;
            align-items: center;
            gap: 10px;
        }

        .control-btn {
            background: none;
            border: none;
            cursor: pointer;
            padding: 5px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: transform 0.2s;
            min-width: 30px;
            user-select: none;

            &:hover {
                transform: scale(1.1);
            }

            svg {
                filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.5));
                pointer-events: none;
            }
        }

        .time-display {
            color: white;
            font-size: 13px;
            text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
            white-space: nowrap;
            user-select: none;
        }

        .playback-rate {
            background: rgba(255, 255, 255, 0.2);
            color: white;
            border: none;
            padding: 5px 10px;
            border-radius: 4px;
            cursor: pointer;
            font-size: 13px;
            user-select: none;

            &:hover {
                background: rgba(255, 255, 255, 0.3);
            }

            option {
                background: #333;
                color: white;
            }
        }
    }
}
</style>
